import React from 'react';
import { Bold, Italic, RotateCcw, RotateCw, Upload, Wand2, Copy, ChevronDown } from 'lucide-react';

export type EditorCardProps = {
  errorCount: number;
  correctedText: string | null;
  wordCount: number;
  isLoading?: boolean;
  toolbarLeft?: React.ReactNode;
  editorSlot: React.ReactNode;
  onCheckGrammar: () => void;
  onUpload: () => void;
  loadingOcrOnly?: boolean;
  onUploadOcr?: () => void;
  onCopyCorrected?: () => void;
};

export const EditorCard: React.FC<EditorCardProps> = ({
  errorCount,
  correctedText,
  wordCount,
  isLoading = false,
  toolbarLeft,
  editorSlot,
  onCheckGrammar,
  onUpload,
  loadingOcrOnly = false,
  onUploadOcr,
  onCopyCorrected,
}) => {
  const hasResults = correctedText != null && correctedText.length > 0;
  const displayCorrected = correctedText ?? '';

  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
      {/* Toolbar — wraps on mobile; actions in own row */}
      <div className="flex-shrink-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 min-w-0">
        <div className="flex flex-wrap gap-2 min-w-0">
          {toolbarLeft ?? (
            <>
              <button
                type="button"
                className="h-9 w-9 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 shrink-0"
                aria-label="Bold"
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                type="button"
                className="h-9 w-9 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 shrink-0"
                aria-label="Italic"
              >
                <Italic className="w-4 h-4" />
              </button>
              <button
                type="button"
                className="h-9 w-9 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 shrink-0"
                aria-label="Undo"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                type="button"
                className="h-9 w-9 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 shrink-0"
                aria-label="Redo"
              >
                <RotateCw className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
        <div className="grid w-full gap-2 min-[360px]:grid-cols-2 sm:w-auto sm:flex sm:gap-3 min-w-0">
          <button
            type="button"
            onClick={onCheckGrammar}
            disabled={isLoading}
            className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm disabled:opacity-50 min-w-0"
          >
            <Wand2 className="w-4 h-4 shrink-0" />
            Check Grammar
          </button>
          <div className="flex gap-2 w-full sm:w-auto">
            {/* <button
              type="button"
              onClick={onUpload}
              disabled={isLoading || loadingOcrOnly}
              className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm disabled:opacity-50 min-w-0"
            >
              <Upload className="w-4 h-4 shrink-0" />
              Upload
            </button> */}
            <button
              type="button"
              onClick={onUploadOcr}
              disabled={isLoading || loadingOcrOnly}
              className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white font-medium text-sm disabled:opacity-50 min-w-0"
            >
              <Upload className="w-4 h-4 shrink-0" />
              {loadingOcrOnly ? 'Extracting...' : 'Upload'}
            </button>
          </div>
        </div>
      </div>

      {/* Section 1 — Original Draft (Mistakes Marked) */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-t border-slate-200 dark:border-slate-800">
        <span className="text-xs font-semibold tracking-wider text-slate-500 dark:text-slate-400 uppercase">
          Original Draft (Mistakes Marked)
        </span>
        <span className="text-xs font-bold px-3 py-1 rounded-full bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800">
          {errorCount} {errorCount === 1 ? 'ERROR' : 'ERRORS'}
        </span>
      </div>
      <div className="flex-1 min-h-0 flex flex-col px-4 pb-4">
        <div className="tamil-editor min-h-[260px] flex-1 overflow-y-auto overflow-x-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
          {editorSlot}
        </div>
      </div>

      {/* Section 2 — Corrected Version (Auto-generated) */}
      {/* <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200 dark:border-slate-800">
        <span className="text-xs font-semibold tracking-wider text-slate-500 dark:text-slate-400 uppercase">
          Corrected Version (Auto-generated)
        </span>
        {hasResults && onCopyCorrected && (
          <button
            type="button"
            onClick={onCopyCorrected}
            className="flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:underline"
          >
            <Copy className="w-4 h-4" />
            Copy
          </button>
        )}
      </div> */}
      {/* <div className="bg-emerald-50 dark:bg-emerald-950/30 px-4 py-4 min-h-[80px]">
        {hasResults ? (
          <p className="text-sm tamil-text whitespace-pre-wrap break-words text-slate-800 dark:text-slate-100 [overflow-wrap:anywhere]">
            {displayCorrected}
          </p>
        ) : (
          <div className="flex flex-col items-center justify-center py-6 text-slate-500 dark:text-slate-400 text-sm text-center">
            <ChevronDown className="w-6 h-6 mb-2 opacity-50" />
            <p>Type or draw above, then click &apos;Check Grammar&apos; or &apos;Evaluate Grammar&apos;…</p>
          </div>
        )}
      </div> */}
      {/* Footer strip */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-emerald-200/60 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-950/20">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
          {wordCount} {wordCount === 1 ? 'Word' : 'Words'}
        </span>
        {errorCount > 0 ? (
          <span className="text-xs font-medium text-rose-600 dark:text-rose-400 flex items-center gap-1">
            {errorCount} {errorCount === 1 ? 'Issue' : 'Issues'} Found
          </span>
        ) : (
          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
            No issues
          </span>
        )}
      </div>
    </div>
  );
};

export default EditorCard;
